import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, CalendarClock, ArrowRight, MapPin } from 'lucide-react';
import { getNewLaunchProjects } from '@/lib/api';

const fmt = (p) => (p >= 1e7 ? `₹${(p / 1e7).toFixed(2)} Cr` : `₹${(p / 1e5).toFixed(0)} L`);

export const NewLaunchProjects = () => {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    getNewLaunchProjects(8).then(setProjects).catch(() => setProjects([]));
  }, []);

  if (!projects.length) return null;

  return (
    <section className="py-16 bg-[#0F172A]" data-testid="new-launch-section">
      <div className="container-luxury">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">RERA-registered · Direct from developers</p>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-white">New Launch Projects</h2>
          </div>
          <Link to="/properties?sort=newest" className="text-white/80 hover:text-[#D4AF37] flex items-center gap-1 text-sm" data-testid="new-launch-view-all">
            View all listings <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory [scrollbar-width:thin]">
          {projects.map((p, i) => (
            <motion.div
              key={p.property_id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="min-w-[280px] md:min-w-[320px] snap-start"
            >
              <Link to={`/properties/${p.property_id}`} className="group block bg-white rounded-sm overflow-hidden h-full" data-testid={`new-launch-card-${p.property_id}`}>
                <div className="relative h-44 overflow-hidden">
                  <img src={p.images?.[0]} alt={p.project_name || p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {p.rera_registered && (
                    <span className="absolute top-3 left-3 flex items-center gap-1 bg-[#0F172A]/90 text-[#D4AF37] text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-sm">
                      <ShieldCheck className="h-3 w-3" /> RERA
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-[#0F172A] line-clamp-1">{p.project_name || p.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 line-clamp-1"><MapPin className="h-3 w-3" /> {p.address}</p>
                  <p className="text-xs text-muted-foreground mt-1">{p.developer}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-bold text-[#0F172A] tabular-nums">From {fmt(p.price)}</span>
                    {p.possession && (
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {p.possession}</span>
                    )}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
